import { Header } from '@features/user-account/components/Header';
import { StyledIconButton } from '@features/user-account/components/StyledIconButton';
import { useAdminApi } from '@hooks/useAdminApi';
import { Box, Stack, Typography } from '@mui/material';
import { ORGANIZATION_LIMIT_COLUMNS, Organization, OrganizationTradeLimit } from '@protos/organization';
import { CellRendererActionButton } from '@shared/components/CellRendererActionButton';
import theme from '@shared/themes/darkTheme';
import { apiErrorMessage } from '@utils/apiError';
import { filterColumnChooserFromMainMenu } from '@utils/contextMenuUtils';
import { ColDef, ICellRendererParams } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo, useState } from 'react';
import { useAsync } from 'react-use';
import { SetCeilingDialog } from './SetCeilingDialog';

type Props = {
  organization: Organization;
  canEdit: boolean;
  linksVersion: number;
};

// Flux owns the ceiling and nothing else. Moving the allocation (allocate and reset) is
// the agent's job over the /agent router, so this surface deliberately offers no way to do
// it, and the position moves only on fills.
export const OrganizationTradeLimits: React.FC<Props> = ({ organization, canEdit, linksVersion }) => {
  const { apiClient } = useAdminApi();
  const [reloadKey, setReloadKey] = useState(0);
  const [isCeilingDialogOpen, setIsCeilingDialogOpen] = useState(false);
  const [limitForCeiling, setLimitForCeiling] = useState<OrganizationTradeLimit | undefined>(undefined);

  const reload = useCallback(() => setReloadKey(key => key + 1), []);

  const limits = useAsync(async () => {
    const rows = await apiClient?.getOrganizationTradeLimits({ organization_id: organization.id });
    return rows ?? [];
  }, [apiClient, organization.id, reloadKey]);

  const onSetCeiling = useCallback((limit?: OrganizationTradeLimit) => {
    setLimitForCeiling(limit);
    setIsCeilingDialogOpen(true);
  }, []);

  const columnDefs = useMemo(() => {
    if (!canEdit) return ORGANIZATION_LIMIT_COLUMNS;
    const actionsColumn: ColDef<OrganizationTradeLimit> = {
      headerName: 'Actions',
      pinned: 'right',
      flex: 1,
      cellRenderer: (params: ICellRendererParams<OrganizationTradeLimit>) =>
        params.data ? (
          <Box display="flex" flexDirection="row" gap={1} alignItems="center" justifyContent="center">
            <CellRendererActionButton
              label="Ceiling"
              onClick={() => onSetCeiling(params.data)}
              backgroundColor="white"
              textColor={theme.palette.background.default}
            />
          </Box>
        ) : null,
    };
    return [...ORGANIZATION_LIMIT_COLUMNS, actionsColumn];
  }, [canEdit, onSetCeiling]);

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Header
          header="Trade Limits"
          helperText="Per-product limits in lots. Flux sets the ceiling here; allocating within it is the agent's own action, and only fills move the position. A product with no row here is not limited at all"
        />
        {/* Adding needs the current rows to tell a new limit from an edit, so it waits
            for them: with none loaded the dialog cannot see what would be overwritten */}
        {canEdit && (
          <StyledIconButton onClick={() => onSetCeiling(undefined)} disabled={limits.loading || !!limits.error}>
            <i className={'ri-add-line'} />
          </StyledIconButton>
        )}
      </Stack>
      {/* A failed read is not an empty limit set. The helper text above says a product
          with no row is unlimited, so an empty grid on an error reads as a fact. */}
      {limits.error ? (
        <Typography fontSize={11} color="error" sx={{ marginTop: '6px' }}>
          Could not load the trade limits: {apiErrorMessage(limits.error, 'the request failed')}. This is not the same as having none.
        </Typography>
      ) : limits.loading ? (
        <Typography fontSize={11} sx={{ marginTop: '6px' }}>
          Loading trade limits...
        </Typography>
      ) : (
        <Box height="300px">
          <AgGridReact
            containerStyle={{ height: '100%', width: '100%', marginTop: '10px' }}
            className="ag-theme-balham-dark custom-grid"
            columnDefs={columnDefs}
            rowData={limits.value ?? []}
            autoSizeStrategy={{ type: 'fitGridWidth' }}
            getMainMenuItems={filterColumnChooserFromMainMenu}
            defaultColDef={{ suppressHeaderMenuButton: true }}
          />
        </Box>
      )}
      {/* Mounted only while open: it resolves the links and a full product page per
          linked group, which is wasted on every organization that is never edited */}
      {isCeilingDialogOpen && (
        <SetCeilingDialog
          organization={organization}
          limit={limitForCeiling}
          existing={limits.value ?? []}
          linksVersion={linksVersion}
          isOpen={isCeilingDialogOpen}
          onClose={() => setIsCeilingDialogOpen(false)}
          onLimitSaved={reload}
        />
      )}
    </>
  );
};
